import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { css } from '@emotion/css';
import { schemas } from '@recurrency/core-api-schema';
import {
  ExcludeDurationOption,
  IntegratedErps,
  RecurrencyReplenishmentMethod,
  TenantUserPermission,
} from '@recurrency/core-api-schema/dist/common/enums';
import { TenantSettingKey } from '@recurrency/core-api-schema/dist/common/tenantSettings';
import { Skeleton } from 'antd';

import { DailyWorkflowProjectedInventoryCard } from 'pages/dailyWorkflow/DailyWorkflowProjectedInventoryCard';
import { getUpdatedMinMaxListFromSearchPlanning } from 'pages/dailyWorkflow/utils';
import { ReplenishmentMethod } from 'pages/purchasing/utils';

import { Button } from 'components/Button';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { NotificationLink } from 'components/Links';
import { Modal } from 'components/Modal';
import { MinMaxInputRadio } from 'components/recipes/MinMaxInputRadio';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { chunkArr } from 'utils/array';
import { capitalize, formatNumber, getErpName, splitIdNameStr } from 'utils/formatting';
import { routes } from 'utils/routes';
import { SearchRequest } from 'utils/search/types';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { isTenantErpTypeEclipse } from 'utils/tenants';
import { getTenantSetting } from 'utils/tenantSettings';
import { MinMaxSubmissionType, PlanningModalType, track, TrackEvent } from 'utils/track';

import { ISODateStr } from 'types/hash-state';

import { ExcludeDurationSelector } from './ExcludeDurationSelector';
import { MultiUpdateReplenishmentSettingsAsyncTable } from './MultiUpdateReplenishmentSettingsModal/MultiUpdateReplenishmentSettingsAsyncTable';
import { MultiUpdateReplenishmentSettingsTable } from './MultiUpdateReplenishmentSettingsTable';
import {
  downloadEclipseMinMaxExcel,
  getIsoDateForExcludeDurationOption,
  getPlanningListSelectionFromFilterSelection,
  MultiModalSelection,
  PlanningItem,
} from './utils';

const FILTER_TO_LIST_SELECTION_LIMIT = 500;

export type MultiUpdateReplenishmentSettingsModalProps = {
  selection: MultiModalSelection;
  onClose: (isSubmit: boolean) => void;
  searchIndexReload: () => void;
  clearSelection: () => void;
  isOpOqVariant: boolean;
  isLoading?: boolean;
};

export function MultiUpdateReplenishmentSettingsModal({
  selection,
  onClose,
  searchIndexReload,
  clearSelection,
  isOpOqVariant,
  isLoading = false,
}: MultiUpdateReplenishmentSettingsModalProps) {
  const [currentTablePage, setCurrentTablePage] = useState(1);
  const [customMin, setCustomMin] = useState<number | undefined>();
  const [customMax, setCustomMax] = useState<number | undefined>();
  const [submissionType, setSubmissionType] = useState<MinMaxSubmissionType>(MinMaxSubmissionType.Recommended);
  const defaultDuration =
    getTenantSetting(TenantSettingKey.FormExcludeDefaultDuration) ?? ExcludeDurationOption.OneMonth;
  const [selectedDuration, setSelectedDuration] = useState<ExcludeDurationOption>(defaultDuration);
  const [customDate, setCustomDate] = useState<ISODateStr | undefined>();
  const { activeTenant } = useGlobalApp();

  const allowExcludeAfterExport = !isOpOqVariant;

  const showExcludeDurationSelector = allowExcludeAfterExport && submissionType === MinMaxSubmissionType.Custom;

  // tooltip is shown if exclude after export is enabled and they are not selecting "don't exclude"
  const showExcludingTooltip = allowExcludeAfterExport && selectedDuration !== ExcludeDurationOption.DontExclude;

  // This variable determines if user has elected to exclude after saving
  const isExcludingAfterSave = showExcludeDurationSelector && selectedDuration !== ExcludeDurationOption.DontExclude;

  /** always use custom min value or recommended min */
  const getExportedMin = (record: PlanningItem): number => {
    if (submissionType === MinMaxSubmissionType.Custom) {
      // This is required to be defined by the time the user submits
      return customMin!;
    }
    return record.recommended_min;
  };

  /** use custom max values for custom, recommended max for min/max versions and current max for op/oq */
  const getExportedMax = (record: PlanningItem): number => {
    if (submissionType === MinMaxSubmissionType.Custom) {
      // This is required to be defined by the time the user submits
      return customMax!;
    }
    if (isOpOqVariant && submissionType === MinMaxSubmissionType.Recommended) {
      return record.current_max;
    }
    return record.recommended_max;
  };

  /** if it is min/max, always specify min/max.
   * if it is op/oq but recommended min/max, then set min/max
   * otherwise, return op/oq
   */
  const exportedReplenishmentMethod = useMemo(() => {
    if (!isOpOqVariant) {
      return RecurrencyReplenishmentMethod.MinMax;
    }
    if (submissionType === MinMaxSubmissionType.RecommendedMinMax) {
      return RecurrencyReplenishmentMethod.MinMax;
    }
    return RecurrencyReplenishmentMethod.OPOQ;
  }, [isOpOqVariant, submissionType]);

  const hasInvalidInputs = () => {
    if (submissionType === MinMaxSubmissionType.Custom) {
      return (
        customMin === undefined ||
        customMin < 0 ||
        customMax === undefined ||
        customMax < 0 ||
        (!isOpOqVariant && customMin > customMax)
      );
    }
    return false;
  };

  const handleMinMaxUpdate = async () => {
    const entityName = 'replenishment settings';
    const hasAsyncMinMaxExportsSetting = getTenantSetting(TenantSettingKey.FeatureAsyncMinMaxExports);
    const submitNotification = createSubmissionNotification({
      entityName: 'Replenishment Settings',
      // 1 minute + 30 sec per 5k records
      expectedWaitSeconds: 60 + Math.round(selection.count / 5_000) * 30,
      erpType: activeTenant.erpType,
      submittingMessage:
        activeTenant.erpType === IntegratedErps.P21 && !hasAsyncMinMaxExportsSetting
          ? `Updating ${entityName} to ${activeTenant.erpType.toUpperCase()}`
          : `Saving ${entityName}`,
    });

    const excludedUntil =
      selectedDuration === ExcludeDurationOption.Custom
        ? customDate
        : getIsoDateForExcludeDurationOption(selectedDuration);

    try {
      onClose(true);

      if (selection.type === 'filter') {
        selection = await getPlanningListSelectionFromFilterSelection(selection, [
          'item_id',
          'item_uid',
          'location',
          'current_min',
          'current_max',
          'recommended_min',
          'recommended_max',
        ]);
      }

      let asyncExportJobId: string | undefined;

      // make api calls in batches of 100k records
      for (const selectionItems of chunkArr(selection.items, 100_000)) {
        const response = await coreApiFetch(schemas.purchasing.postUpdateItemLocationMinMax, {
          bodyParams: {
            asyncExportJobId,
            updates: selectionItems.map((record) => ({
              itemId: record.item_id,
              itemUid: record.item_uid,
              locationId: splitIdNameStr(record.location).foreignId,
              newReplenishmentMethod: exportedReplenishmentMethod,
              newMin: getExportedMin(record),
              newMax: getExportedMax(record),
            })),
            excluded: isExcludingAfterSave,
            excludedUntil: excludedUntil || undefined,
          },
        });
        asyncExportJobId = response.data.asyncExportJobId;
      }

      // Eclipse is updated by importing a file, so after saving the values the user downloads that file
      if (isTenantErpTypeEclipse(activeTenant.erpType)) {
        await downloadEclipseMinMaxExcel(
          selection.items.map((record) => ({
            itemId: record.item_id,
            locationId: splitIdNameStr(record.location).foreignId,
            newMin: getExportedMin(record),
            newMax: getExportedMax(record),
          })),
        );
      }

      track(TrackEvent.DemandPlanning_MinMax_Submission, {
        modalType: isOpOqVariant ? PlanningModalType.MultiOpOq : PlanningModalType.MultiMinMax,
        itemCount: selection.count,
        submissionType,
      });
      clearSelection();
      searchIndexReload();
      submitNotification.success({
        description:
          asyncExportJobId && hasAsyncMinMaxExportsSetting
            ? (notificationKey) => (
                <>
                  <div>
                    {capitalize(entityName)} will export to {getErpName(activeTenant.erpType)} in background.
                  </div>
                  <NotificationLink notificationKey={notificationKey} to={routes.monitoring.exportJobs()}>
                    View Export Jobs
                  </NotificationLink>
                </>
              )
            : undefined,
      });
    } catch (err) {
      submitNotification.error(err);
    }
  };

  const minMaxUpdatesData =
    selection.type === 'list'
      ? getUpdatedMinMaxListFromSearchPlanning(
          selection.items,
          submissionType === MinMaxSubmissionType.Custom ? customMin : undefined,
          submissionType === MinMaxSubmissionType.Custom ? customMax : undefined,
        )
      : [];

  const modalTitle = `Updating Replenishment Settings for ${formatNumber(selection.count)} Items`;

  if (isLoading) {
    return (
      <Modal visible width={900} title={modalTitle} onCancel={() => onClose(false)} destroyOnClose footer={null}>
        <Skeleton />
      </Modal>
    );
  }

  return (
    <Modal
      visible
      title={modalTitle}
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button
            onClick={handleMinMaxUpdate}
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionExportMinMax]}
            disabled={hasInvalidInputs()}
          >
            {activeTenant.erpType === IntegratedErps.P21
              ? `Send to ${getErpName(activeTenant.erpType)}`
              : isTenantErpTypeEclipse(activeTenant.erpType)
              ? 'Save and Download'
              : 'Save'}
            {isExcludingAfterSave ? ' + Exclude' : ''}
          </Button>
        </>
      }
      width={900}
    >
      <FlexSpace direction="column" alignItems="stretch">
        <MinMaxInputRadio
          isOpOqVariant={isOpOqVariant}
          submissionType={submissionType}
          onChangeSubmissionType={setSubmissionType}
          customMin={customMin}
          customMax={customMax}
          setCustomMin={setCustomMin}
          setCustomMax={setCustomMax}
          showExcludingTooltip={showExcludingTooltip}
          selectedDuration={selectedDuration}
        />

        {showExcludeDurationSelector && (
          <ExcludeDurationSelector
            overrideClassName={css`
              margin-top: 8px;
              margin-left: 8px;
            `}
            selectedDuration={selectedDuration}
            setSelectedDuration={setSelectedDuration}
            customDate={customDate}
            setCustomDate={setCustomDate}
            includeDontExcludeOption
          />
        )}
        <DividerLine />
        {selection.type === 'list' ? (
          <>
            <DailyWorkflowProjectedInventoryCard
              minMaxUpdates={{ items: minMaxUpdatesData, totalCount: minMaxUpdatesData.length }}
            />
            <MultiUpdateReplenishmentSettingsAsyncTable
              currentReplenishmentMethod={isOpOqVariant ? ReplenishmentMethod.OPOQ : ReplenishmentMethod.MinMax}
              tableData={minMaxUpdatesData}
              isLoading={false}
              currentPage={currentTablePage}
              setCurrentPage={setCurrentTablePage}
            />
          </>
        ) : (
          <MultiUpdateReplenishmentSettingsTable
            selection={selection}
            bulkUpdate={{
              type: 'minMax',
              customMin,
              customMax,
              isOpOqVariant,
              submissionType,
            }}
          />
        )}
      </FlexSpace>
    </Modal>
  );
}

/**
 * Wrapper component for the replenishment settings modal that, if selection.count is <= FILTER_TO_LIST_SELECTION_LIMIT,
 * handles fetching items from search request and converting filter selection to list selection
 */
export function MultiUpdateReplenishmentSettingsModalWListSelection({
  searchRequest,
  ...props
}: MultiUpdateReplenishmentSettingsModalProps & {
  searchRequest: SearchRequest;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [updatedSelection, setUpdatedSelection] = useState(props.selection);

  // Fetch items from search request and convert filter selection to list selection
  const fetchItems = useCallback(async () => {
    if (
      updatedSelection?.type === 'filter' &&
      updatedSelection.count > 0 &&
      updatedSelection.count <= FILTER_TO_LIST_SELECTION_LIMIT
    ) {
      setIsLoading(true);
      const planningItems = (
        await getPlanningListSelectionFromFilterSelection(
          {
            type: 'filter',
            query: searchRequest.query,
            filters: searchRequest.filters,
            sortBy: searchRequest.sortBy,
            secondarySortBy: searchRequest.secondarySortBy,
            count: 0,
          },
          [
            'item_uid',
            'location',
            'recommended_max',
            'recommended_min',
            'current_max',
            'current_min',
            'unit_cost',
            'recurrency_replenishment_method',
            'item_name',
            'item_id',
            'replenishment_method',
          ],
        )
      ).items;

      setUpdatedSelection({
        type: 'list',
        items: planningItems,
        count: planningItems.length,
      });
    }
    setIsLoading(false);
  }, [searchRequest, updatedSelection.count, updatedSelection?.type]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  return <MultiUpdateReplenishmentSettingsModal {...props} isLoading={isLoading} selection={updatedSelection} />;
}
